/**
 * `/settings/*` — a layout route with the section nav, so every settings screen
 * shares one chrome and the sub-routes stay small.
 */

import { cn } from '@crm/ui';
import { createFileRoute, Link, Outlet } from '@tanstack/react-router';
import { PageHeader } from '../../components/page.tsx';
import { RouteErrorPanel } from '../../components/route-error-boundary.tsx';

const SECTIONS: readonly { readonly to: string; readonly label: string }[] = [
  { to: '/settings/profile', label: 'Profile' },
  { to: '/settings/accounts', label: 'Connected accounts' },
  { to: '/settings/organization', label: 'Organization' },
  { to: '/settings/sharing', label: 'Sharing' },
  { to: '/settings/privacy', label: 'Privacy & recording' },
  { to: '/settings/devices', label: 'Devices' },
];

export const Route = createFileRoute('/_authed/settings')({
  component: SettingsLayout,
  errorComponent: RouteErrorPanel,
});

function SettingsLayout() {
  return (
    <>
      <PageHeader title="Settings" />
      <div className="flex min-h-0 flex-1">
        <nav className="w-56 shrink-0 border-r border-border p-3">
          <ul className="flex flex-col gap-0.5">
            {SECTIONS.map((section) => (
              <li key={section.to}>
                <Link
                  to={section.to}
                  className={cn(
                    'block rounded-md px-2 py-1.5 text-sm text-muted-foreground',
                    'hover:bg-accent hover:text-accent-foreground',
                  )}
                  activeProps={{ className: 'bg-accent text-accent-foreground font-medium' }}
                >
                  {section.label}
                </Link>
              </li>
            ))}
          </ul>
          {/* Hermes and enrichment are in design §2.2's inventory but have no
              backing configuration yet; they are omitted rather than listed as
              dead links. Privacy landed with the recorder integration, because
              that is the track that gave the do-not-record flag a write path. */}
        </nav>
        <div className="min-w-0 flex-1 overflow-auto p-4">
          <Outlet />
        </div>
      </div>
    </>
  );
}
